import { useState, useRef, useCallback } from 'preact/hooks';
import { useRecorder } from './useRecorder';
import { useWavRecorder, isAudioWorkletSupported } from './useWavRecorder';

interface Props {
  onRecorded: (chunks: Blob[], recordingId?: string) => void;
  onCancel: () => void;
  onChunk?: (chunk: Blob) => void | Promise<void>;
}

/**
 * Phase 6.1 toggle: AudioWorklet-based PCM/WAV capture replaces MediaRecorder
 * (Opus/WebM) when both:
 *  (a) the env flag VITE_USE_WAV_RECORDER is "true" (or localStorage
 *      'voice-use-wav' is "1" — operator opt-in for soak; takes effect on
 *      next page reload since the choice is locked at module load), AND
 *  (b) the runtime supports AudioWorklet (iOS Safari <14.1 falls back).
 * MediaRecorder retains every Phase 1-4.5 protection so the fallback is
 * fully functional, just with WebM-fragility risk.
 */
function shouldUseWavRecorder(): boolean {
  if (!isAudioWorkletSupported()) return false;
  if (typeof window !== 'undefined') {
    try {
      if (window.localStorage?.getItem('voice-use-wav') === '1') return true;
    } catch { /* private mode / disabled storage */ }
  }
  return import.meta.env.VITE_USE_WAV_RECORDER === 'true';
}

// Locked at module load so the hook call order stays statically stable for
// React's Hooks rules. Flipping the localStorage flag requires a page reload.
const useChosenRecorder = shouldUseWavRecorder() ? useWavRecorder : useRecorder;

export function useRecorderDock({ onRecorded, onCancel, onChunk }: Props) {
  const [isLocked, setIsLocked] = useState(false);
  const [atSoftCap, setAtSoftCap] = useState(false);

  // Keep stable refs so callbacks don't go stale inside the recorder hook
  const onRecordedRef = useRef(onRecorded);
  const onCancelRef = useRef(onCancel);
  const onChunkRef = useRef(onChunk);
  onRecordedRef.current = onRecorded;
  onCancelRef.current = onCancel;
  onChunkRef.current = onChunk;

  // Keep recordingId in a ref so the onHardCap closure below always sees the current value.
  const recordingIdRef = useRef<string | undefined>(undefined);

  const { recording, durationMs, levelBars, extendCap: recorderExtendCap, start, stop, recordingId } = useChosenRecorder({
    onSoftCap: () => setAtSoftCap(true),
    onHardCap: (chunks) => {
      setIsLocked(false);
      setAtSoftCap(false);
      if (chunks.length > 0) onRecordedRef.current(chunks, recordingIdRef.current);
    },
    onChunk: (chunk) => onChunkRef.current?.(chunk),
  });
  recordingIdRef.current = recordingId;

  const startRecording = useCallback(async (onError?: (msg: string) => void) => {
    setIsLocked(false);
    setAtSoftCap(false);
    await start(onError);
  }, [start]);

  const stopRecording = useCallback(async () => {
    const chunks = await stop();
    const rid = recordingIdRef.current;
    setIsLocked(false);
    setAtSoftCap(false);
    if (chunks.length > 0) onRecordedRef.current(chunks, rid);
  }, [stop]);

  const lock = useCallback(() => setIsLocked(true), []);

  const cancel = useCallback(async () => {
    await stop();
    setIsLocked(false);
    setAtSoftCap(false);
    onCancelRef.current();
  }, [stop]);

  const extendCap = useCallback(() => {
    setAtSoftCap(false);
    recorderExtendCap();
  }, [recorderExtendCap]);

  return {
    isRecording: recording,
    isLocked,
    durationMs,
    levelBars,
    atSoftCap,
    recordingId,
    start: startRecording,
    stop: stopRecording,
    lock,
    cancel,
    extendCap,
  };
}
